
import { supabase } from "@/integrations/supabase/client";
import { AISummary } from "@/types";
import { Tables } from "@/integrations/supabase/types";

// Create an AI summary
export const createAISummary = async (summary: Omit<AISummary, "id" | "created_at">): Promise<Tables<"ai_summaries">> => {
  const { data, error } = await supabase
    .from("ai_summaries")
    .insert(summary)
    .select()
    .single();

  if (error) throw error;
  return data;
};

// Get AI summary for a lead
export const getAISummaryByLeadId = async (leadId: string): Promise<Tables<"ai_summaries"> | null> => {
  const { data, error } = await supabase
    .from("ai_summaries")
    .select("*")
    .eq("lead_id", leadId)
    .maybeSingle();

  if (error) throw error;
  return data;
};

// Generate an AI summary from a transcript
export const generateAISummary = async (leadId: string, transcript: string): Promise<AISummary> => {
  try {
    console.log(`Generating AI summary for lead ID: ${leadId}`);
    
    // Call Claude or GPT-4 via edge function to analyze the transcript
    const { data, error } = await supabase.functions.invoke("generate-summary", {
      body: { transcript },
    });

    if (error) throw error;
    if (!data.success) throw new Error(data.error || "Failed to generate AI summary");

    // Create the AI summary in the database
    const aiSummary: Omit<AISummary, "id" | "created_at"> = {
      lead_id: leadId,
      summary: data.intent_summary,
      intent_score: data.intent_score.toString() || "Unknown", // Ensure this is never undefined
      next_action: data.recommended_follow_up,
      model_used: data.model_used || "claude-sonnet" // Ensure this is never undefined
    };

    return await createAISummary(aiSummary) as AISummary;
  } catch (error) {
    console.error("Error generating AI summary:", error);
    // Create a basic summary if AI analysis fails
    const fallbackSummary: Omit<AISummary, "id" | "created_at"> = {
      lead_id: leadId,
      summary: "No AI analysis available",
      intent_score: "Unknown",
      next_action: "Follow up manually",
      model_used: "fallback"
    };
    return await createAISummary(fallbackSummary) as AISummary;
  }
};
